import * as THREE from 'three'
import { Canvas } from '@react-three/fiber'
import { Suspense, lazy, useEffect } from 'react'
import Scene from './components/Scene'
import LoadingScreen from './components/LoadingScreen'
import DashboardPanels from './components/DashboardPanels'
import PostProcessingStack from './components/PostProcessingStack'

const DevConsoleToggle = lazy(() => import('./components/DevConsoleToggle'))
const UnifiedDevPanel = lazy(() => import('./components/UnifiedDevPanel'))
const HelpModal = lazy(() => import('./components/HelpModal'))
const ProjectInfoPanel = lazy(() => import('./components/ProjectInfoPanel'))
const DayNightToggle = lazy(() => import('./components/DayNightToggle'))
const TelemetryGraphPanel = lazy(() => import('./components/TelemetryGraphPanel'))
import { useDashboardPanelStore } from './stores/useDashboardPanelStore'
import { useDecalStore } from './stores/useDecalStore'
import { usePostProcessingStore } from './stores/usePostProcessingStore'
import { useDevModeStore } from './stores/useDevModeStore'
import { useDevGateStore } from './stores/useDevGateStore'
import { usePOIHoverStore } from './stores/usePOIHoverStore'
import { useLoadingStore } from './stores/useLoadingStore'
import { useTelemetryStore } from './stores/useTelemetryStore'
import { useUnifiedPanelStore } from './stores/useUnifiedPanelStore'
import { useDevModeShortcuts } from './hooks/useDevModeShortcuts'
import { useDayNightAnimation } from './hooks/useDayNightAnimation'
import { useAutoRotate } from './hooks/useAutoRotate'

function App() {
  const { backgroundTopColor, backgroundBottomColor } = useDevModeStore()
  const { isUnlocked, unlock } = useDevGateStore()
  const { isVisible: isLoadingVisible } = useLoadingStore()
  const isUnifiedPanelOpen = useUnifiedPanelStore((s) => s.isOpen)

  // Dev mode keyboard shortcuts (Ctrl+Y toggle, Esc close)
  useDevModeShortcuts()

  // Day/Night cycle animation
  useDayNightAnimation()

  // Auto-rotate on inactivity
  useAutoRotate()

  // Load data from server on mount
  const loadCSV = useTelemetryStore((s) => s.loadCSV)
  const loadDashboardPanelConfig = useDashboardPanelStore((s) => s.loadFromServer)
  const loadDecalConfig = useDecalStore((s) => s.loadFromServer)
  const loadPostProcessingConfig = usePostProcessingStore((s) => s.loadFromServer)
  const loadPresets = useDevModeStore((s) => s.loadPresets)
  const loadMaterialHighlight = usePOIHoverStore((s) => s.loadMaterialHighlight)
  useEffect(() => {
    loadCSV()
    loadDashboardPanelConfig()
    loadDecalConfig()
    loadPostProcessingConfig()
    loadPresets()
    loadMaterialHighlight()
  }, [
    loadCSV,
    loadDashboardPanelConfig,
    loadDecalConfig,
    loadPostProcessingConfig,
    loadPresets,
    loadMaterialHighlight,
  ])

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Secret dev mode unlock: Ctrl+Shift+Alt+D or Cmd+Shift+Alt+D (always works)
      // Use e.code for reliable detection (Option key changes e.key on Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.altKey && e.code === 'KeyD') {
        e.preventDefault()
        unlock()
        console.log('Dev mode unlocked')
        return
      }
      // Lock dev mode: Ctrl+Shift+Alt+L or Cmd+Shift+Alt+L (for testing)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.altKey && e.code === 'KeyL') {
        e.preventDefault()
        useDevGateStore.getState().lock()
        console.log('Dev mode locked')
        return
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [unlock])

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(to bottom, ${backgroundTopColor}, ${backgroundBottomColor})`,
        position: 'relative',
      }}
    >
      {/* Main Canvas */}
      <Canvas
        shadows={{ type: THREE.VSMShadowMap }}
        gl={{ toneMapping: THREE.NoToneMapping }}
        camera={{
          position: [-0.12, 10.09, -8.26],
          fov: 50,
          near: 1,
          far: 500,
        }}
      >
        <Suspense fallback={null}>
          <Scene />
        </Suspense>
        <Suspense fallback={null}>
          <DashboardPanels />
        </Suspense>
        <PostProcessingStack />
      </Canvas>

      {/* Dev UI - only rendered when unlocked */}
      {isUnlocked && (
        <Suspense fallback={null}>
          {/* Dev Console Toggle Button - hide when unified panel is open */}
          {!isUnifiedPanelOpen && <DevConsoleToggle />}

          {/* Unified Dev Panel (all dev tools in tabs) */}
          <UnifiedDevPanel />

          {/* Global Help Modal */}
          <HelpModal />
        </Suspense>
      )}

      {/* Day/Night Toggle + Time Display - visible to all users */}
      <Suspense fallback={null}>
        <DayNightToggle />
      </Suspense>

      {/* Telemetry Graph Panel - shows on POI click */}
      <Suspense fallback={null}>
        <TelemetryGraphPanel />
      </Suspense>

      {/* Project Info Panel - visible to all users */}
      <Suspense fallback={null}>
        <ProjectInfoPanel />
      </Suspense>

      {/* Loading Screen */}
      {isLoadingVisible && <LoadingScreen />}
    </div>
  )
}

export default App
